/**
 * DeleteConfirmationDrawer – Full-screen drawer for template deletion confirmation.
 *
 * Replaces the previous DeleteConfirmationModal.
 * Restricted to super-admin; requires explicit confirmation.
 * Explains the impact of deletion (breaking references).
 * Prefers archive over delete.
 *
 * Uses Mantine Drawer matching theme.ts styling.
 */
import { Box, Button, Drawer, Group, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconX } from '@tabler/icons-react';
import type { TemplateListItem } from '../types';

interface DeleteConfirmationDrawerProps {
  opened: boolean;
  onClose: () => void;
  template: TemplateListItem | null;
  onConfirm: () => void;
  loading?: boolean;
}

export function DeleteConfirmationDrawer({
  opened,
  onClose,
  template,
  onConfirm,
  loading = false,
}: DeleteConfirmationDrawerProps): JSX.Element {
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      withCloseButton={false}
      styles={{
        body: { padding: 0, height: '100%', display: 'flex', flexDirection: 'column' },
        content: { display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Header */}
      <Box
        px="lg"
        py="sm"
        style={{
          borderBottom: '1px solid #E5E7EB',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#FFFFFF',
        }}
      >
        <Title order={5}>Delete Template</Title>
        <Button variant="subtle" color="gray" size="compact-sm" onClick={onClose}>
          <IconX size={18} />
        </Button>
      </Box>

      {/* Content */}
      <Box style={{ flex: 1, padding: 'var(--mantine-spacing-lg)' }}>
        <Stack gap="md">
          <Group gap="sm" align="flex-start" wrap="nowrap">
            <IconAlertTriangle size={24} color="var(--mantine-color-red-6)" style={{ flexShrink: 0, marginTop: 2 }} />
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Are you sure you want to delete &ldquo;{template?.title}&rdquo;?
              </Text>
              <Text size="sm" c="dimmed">
                This action cannot be undone. Deleting this template may break references
                in existing notes or documents that use it.
              </Text>
              <Text size="sm" c="dimmed">
                <Text component="span" fw={600} c="dimmed">
                  Recommendation:
                </Text>{' '}
                Consider archiving this template instead of deleting it.
                Archived templates are hidden from active use but preserve
                all existing references.
              </Text>
            </Stack>
          </Group>

          <Group justify="flex-end">
            <Button variant="outline" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button color="red" onClick={onConfirm} loading={loading}>
              Delete permanently
            </Button>
          </Group>
        </Stack>
      </Box>
    </Drawer>
  );
}
